import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, Building2, Clock3, MapPin, Palette, Save, ShieldCheck, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { readableForeground } from '../../lib/themeColors';
import { useApiCollection } from '../../lib/useApiCollection';
import type { AttendanceCheckinExportColumn, Organization, OrganizationSettings } from '../../types/domain';

const attendanceExportColumns: { key: AttendanceCheckinExportColumn; label: string }[] = [
  { key: 'serial_number', label: 'Sr. (Serial number)' },
  { key: 'brand_ambassador', label: 'Brand ambassador' },
  { key: 'store', label: 'Store' },
  { key: 'attendance_status', label: 'Attendance status' },
  { key: 'captured_at', label: 'Time (Captured on device)' },
  { key: 'gps_evidence', label: 'GPS evidence' },
  { key: 'validation', label: 'Validation' },
  { key: 'review', label: 'Review' },
  { key: 'selfie', label: 'Selfie' },
];

interface SettingsDraft {
  reporting_currency: 'PKR';
  attendance_checkin_export_columns: AttendanceCheckinExportColumn[];
  theme_primary_color: string;
  theme_accent_color: string;
  use_agency_logo: boolean;
  use_agency_name: boolean;
  default_allowed_radius_m: string;
  default_checkin_open_minutes: string;
  default_late_after_minutes: string;
  default_checkout_grace_minutes: string;
}

const emptyDraft: SettingsDraft = {
  reporting_currency: 'PKR',
  attendance_checkin_export_columns: attendanceExportColumns.map(({ key }) => key),
  theme_primary_color: '#122F26',
  theme_accent_color: '#F0C968',
  use_agency_logo: false,
  use_agency_name: false,
  default_allowed_radius_m: '50',
  default_checkin_open_minutes: '30',
  default_late_after_minutes: '15',
  default_checkout_grace_minutes: '120',
};

function toDraft(settings: OrganizationSettings): SettingsDraft {
  return {
    reporting_currency: settings.reporting_currency,
    attendance_checkin_export_columns: [...settings.attendance_checkin_export_columns],
    theme_primary_color: settings.theme_primary_color,
    theme_accent_color: settings.theme_accent_color,
    use_agency_logo: settings.use_agency_logo,
    use_agency_name: settings.use_agency_name,
    default_allowed_radius_m: String(settings.default_allowed_radius_m),
    default_checkin_open_minutes: String(settings.default_checkin_open_minutes),
    default_late_after_minutes: String(settings.default_late_after_minutes),
    default_checkout_grace_minutes: String(settings.default_checkout_grace_minutes),
  };
}

export function SettingsPage() {
  const {
    accessToken,
    capabilities,
    isPlatformAdmin,
    selectedWorkspace,
    updateWorkspaceAppearance,
  } = useAuth();
  const queryClient = useQueryClient();
  const [platformOrganizationId, setPlatformOrganizationId] = useState('');
  const [draftState, setDraftState] = useState<{
    organizationId: string;
    values: SettingsDraft;
  } | null>(null);
  const organizations = useApiCollection<Organization>(
    'settings-organizations',
    '/organizations/?page_size=200&ordering=name',
    accessToken,
    isPlatformAdmin && !selectedWorkspace?.organizationId,
  );
  const configurableOrganizations = useMemo(
    () => (organizations.data?.results ?? []).filter((organization) => (
      organization.type === 'agency' || organization.type === 'client'
    )),
    [organizations.data?.results],
  );

  const selectedPlatformOrganizationId = platformOrganizationId
    || configurableOrganizations[0]?.id
    || '';
  const organizationId = selectedWorkspace?.organizationId ?? selectedPlatformOrganizationId;
  const settings = useQuery({
    queryKey: ['organization-settings', organizationId, accessToken],
    enabled: Boolean(accessToken && organizationId),
    queryFn: () => apiRequest<OrganizationSettings>(
      `/organizations/${organizationId}/settings/`,
      accessToken ? { accessToken } : {},
    ),
  });

  const draft = draftState?.organizationId === organizationId
    ? draftState.values
    : settings.data
      ? toDraft(settings.data)
      : emptyDraft;

  const canManage = capabilities.includes('settings:manage');
  const saveSettings = useMutation({
    mutationFn: () => apiRequest<OrganizationSettings>(
      `/organizations/${organizationId}/settings/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify({
          reporting_currency: draft.reporting_currency,
          attendance_checkin_export_columns: draft.attendance_checkin_export_columns,
          theme_primary_color: draft.theme_primary_color,
          theme_accent_color: draft.theme_accent_color,
          use_agency_logo: draft.use_agency_logo,
          use_agency_name: draft.use_agency_name,
          default_allowed_radius_m: Number(draft.default_allowed_radius_m),
          default_checkin_open_minutes: Number(draft.default_checkin_open_minutes),
          default_late_after_minutes: Number(draft.default_late_after_minutes),
          default_checkout_grace_minutes: Number(draft.default_checkout_grace_minutes),
        }),
      },
    ),
    onSuccess: async (updated) => {
      setDraftState({ organizationId, values: toDraft(updated) });
      updateWorkspaceAppearance(
        organizationId,
        updated.theme_primary_color,
        updated.theme_accent_color,
        updated.use_agency_logo,
        updated.use_agency_name,
      );
      await queryClient.invalidateQueries({ queryKey: ['organization-settings', organizationId] });
    },
  });

  function updateDraft(changes: Partial<SettingsDraft>) {
    setDraftState({
      organizationId,
      values: { ...draft, ...changes },
    });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (canManage && draft.attendance_checkin_export_columns.length && !saveSettings.isPending) {
      saveSettings.mutate();
    }
  }

  const loading = organizations.isLoading || (Boolean(organizationId) && settings.isLoading);
  const error = organizations.isError || settings.isError;
  const empty = !organizationId;

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Settings</h1>
          <p>Manage workspace appearance, report columns, operational defaults, and access.</p>
        </div>
      </header>

      {isPlatformAdmin && !selectedWorkspace?.organizationId ? (
        <section className="content-card settings-workspace-card">
          <div className="card-heading">
            <div><h2>Organisation</h2><p>Select the agency or client whose defaults you want to manage.</p></div>
            <Building2 size={22} />
          </div>
          <label className="settings-organisation-picker">
            Organisation
            <select
              aria-label="Settings organisation"
              value={selectedPlatformOrganizationId}
              onChange={(event) => setPlatformOrganizationId(event.target.value)}
            >
              <option value="">Select organisation</option>
              {configurableOrganizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name} · {organization.type}
                </option>
              ))}
            </select>
          </label>
        </section>
      ) : null}

      <DataPanel loading={loading} error={error} empty={empty}>
        <div className="settings-layout">
          <section className="content-card settings-defaults-card">
            <div className="card-heading">
              <div>
                <h2>Organisation settings</h2>
                <p>Apply this organisation&rsquo;s identity, report preferences, and operational defaults.</p>
              </div>
              <Palette size={22} />
            </div>
            <form className="management-form" onSubmit={submit}>
              <div className="settings-section-heading">
                <div><h3>Workspace colours</h3><p>These colours apply whenever this organisation&rsquo;s workspace is selected.</p></div>
                <Palette size={19} />
              </div>
              <div className="form-grid settings-theme-grid">
                <div className="settings-theme-colour-field">
                  <label htmlFor="theme-primary-colour">Primary colour</label>
                  <div className="brand-theme-colour-inputs">
                    <input
                      aria-label="Primary colour picker"
                      disabled={!canManage}
                      onChange={(event) => updateDraft({ theme_primary_color: event.target.value.toUpperCase() })}
                      type="color"
                      value={/^#[0-9A-Fa-f]{6}$/.test(draft.theme_primary_color) ? draft.theme_primary_color : '#122F26'}
                    />
                    <input
                      disabled={!canManage}
                      id="theme-primary-colour"
                      maxLength={7}
                      onChange={(event) => updateDraft({ theme_primary_color: event.target.value })}
                      pattern="#[0-9A-Fa-f]{6}"
                      required
                      type="text"
                      value={draft.theme_primary_color}
                    />
                  </div>
                  <small>Used for navigation and primary actions. Text adapts automatically for readability.</small>
                </div>
                <div className="settings-theme-colour-field">
                  <label htmlFor="theme-accent-colour">Accent colour</label>
                  <div className="brand-theme-colour-inputs">
                    <input
                      aria-label="Accent colour picker"
                      disabled={!canManage}
                      onChange={(event) => updateDraft({ theme_accent_color: event.target.value.toUpperCase() })}
                      type="color"
                      value={/^#[0-9A-Fa-f]{6}$/.test(draft.theme_accent_color) ? draft.theme_accent_color : '#F0C968'}
                    />
                    <input
                      disabled={!canManage}
                      id="theme-accent-colour"
                      maxLength={7}
                      onChange={(event) => updateDraft({ theme_accent_color: event.target.value })}
                      pattern="#[0-9A-Fa-f]{6}"
                      required
                      type="text"
                      value={draft.theme_accent_color}
                    />
                  </div>
                  <small>Used for active navigation, highlights, and progress indicators.</small>
                </div>
                <div
                  className="settings-theme-preview wide-field"
                  style={{
                    backgroundColor: draft.theme_primary_color,
                    color: readableForeground(draft.theme_primary_color),
                  }}
                >
                  <span style={{ color: draft.theme_accent_color }}>Workspace preview</span>
                  <strong>Brand Field Crew</strong>
                  <em style={{ backgroundColor: draft.theme_accent_color, color: draft.theme_primary_color }}>Active</em>
                </div>
              </div>
              {settings.data?.organization_type === 'agency' ? (
                <div className="settings-section-heading settings-section-divider">
                  <div>
                    <h3>Agency branding</h3>
                    <p>Select the agency logo and name you want to show in this workspace&rsquo;s sidebar.</p>
                    <div className="settings-branding-options">
                    <label className="checkbox-field settings-branding-toggle">
                      <input
                        checked={draft.use_agency_logo}
                        disabled={!canManage || !settings.data.organization_logo}
                        onChange={(event) => updateDraft({ use_agency_logo: event.target.checked })}
                        type="checkbox"
                      />
                      Show agency logo
                    </label>
                    <label className="checkbox-field settings-branding-toggle">
                      <input
                        checked={draft.use_agency_name}
                        disabled={!canManage}
                        onChange={(event) => updateDraft({ use_agency_name: event.target.checked })}
                        type="checkbox"
                      />
                      Show agency name
                    </label>
                    </div>
                    {!settings.data.organization_logo ? (
                      <small className="settings-branding-hint">
                        Upload an agency logo in <Link to="/organisations">Organisation profile</Link> before enabling this option.
                      </small>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className="settings-section-heading settings-section-divider">
                <div><h3>Operational defaults</h3><p>These values prefill new campaign stores and shifts. Existing records are never changed.</p></div>
                <Clock3 size={19} />
              </div>
              <div className="form-grid">
                <label>
                  Reporting currency
                  <select disabled={!canManage} value={draft.reporting_currency} onChange={(event) => updateDraft({ reporting_currency: event.target.value as 'PKR' })}>
                    <option value="PKR">PKR — Pakistani rupee</option>
                  </select>
                  <small>The pilot currently supports PKR reporting.</small>
                </label>
                <label>
                  Default allowed radius (metres)
                  <input disabled={!canManage} max="1000" min="10" required type="number" value={draft.default_allowed_radius_m} onChange={(event) => updateDraft({ default_allowed_radius_m: event.target.value })} />
                </label>
                <label>
                  Check-in opens (minutes before)
                  <input disabled={!canManage} max="1440" min="0" required type="number" value={draft.default_checkin_open_minutes} onChange={(event) => updateDraft({ default_checkin_open_minutes: event.target.value })} />
                </label>
                <label>
                  Late after (minutes)
                  <input disabled={!canManage} max="1440" min="0" required type="number" value={draft.default_late_after_minutes} onChange={(event) => updateDraft({ default_late_after_minutes: event.target.value })} />
                </label>
                <label>
                  Checkout grace (minutes)
                  <input disabled={!canManage} max="1440" min="0" required type="number" value={draft.default_checkout_grace_minutes} onChange={(event) => updateDraft({ default_checkout_grace_minutes: event.target.value })} />
                </label>
              </div>
              <div className="settings-section-heading settings-section-divider">
                <div>
                  <h3>Daily check-in XLSX columns</h3>
                  <p>Choose the columns included in future attendance Excel downloads from this organisation&rsquo;s workspace. Unchecked columns are omitted, not hidden. Other reports are unchanged.</p>
                </div>
              </div>
              <fieldset className="settings-export-options" disabled={!canManage || saveSettings.isPending}>
                <legend>Columns to include</legend>
                <div className="settings-export-columns">
                  {attendanceExportColumns.map(({ key, label }) => (
                    <label className="checkbox-field" key={key}>
                      <input
                        checked={draft.attendance_checkin_export_columns.includes(key)}
                        onChange={(event) => updateDraft({
                          attendance_checkin_export_columns: attendanceExportColumns
                            .filter((column) => column.key === key
                              ? event.target.checked
                              : draft.attendance_checkin_export_columns.includes(column.key))
                            .map((column) => column.key),
                        })}
                        type="checkbox"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <p className="settings-branding-hint">Keep at least one column selected. Column order stays fixed. If Selfie is unchecked, photos are not loaded or embedded.</p>
              {!draft.attendance_checkin_export_columns.length ? <p className="form-error" role="alert">Select at least one attendance export column.</p> : null}
              {saveSettings.isError ? <p className="form-error" role="alert">{getApiErrorMessage(saveSettings.error)}</p> : null}
              {saveSettings.isSuccess ? <p className="form-success" role="status">Organisation settings saved.</p> : null}
              {canManage ? (
                <div className="form-actions">
                  <button className="button primary" disabled={saveSettings.isPending || !draft.attendance_checkin_export_columns.length} type="submit">
                    {saveSettings.isPending ? <InlineSpinner /> : <Save size={16} />}
                    {saveSettings.isPending ? 'Saving…' : 'Save settings'}
                  </button>
                </div>
              ) : null}
            </form>
          </section>

          <aside className="settings-side-column">
            <section className="content-card settings-link-card">
              <div className="card-heading"><div><h2>Organisation access</h2><p>Profile and membership management remain in Organisations.</p></div><UsersRound size={21} /></div>
              <div className="settings-links">
                <Link className="button secondary" to="/organisations"><Building2 size={16} /> Organisation profile</Link>
                {organizationId ? <Link className="button secondary" to={`/organisations/${organizationId}/members`}><UsersRound size={16} /> Members and roles</Link> : null}
              </div>
            </section>
            <section className="content-card settings-access-card">
              <div className="card-heading"><div><h2>Your capabilities</h2><p>Server-issued access for the selected workspace.</p></div><ShieldCheck size={21} /></div>
              <div className="capability-list">
                {capabilities.map((capability) => <span key={capability}>{capability}</span>)}
              </div>
            </section>
            <section className="content-card settings-note-card">
              <MapPin size={20} />
              <div><strong>Safe defaults</strong><p>Managers can still adjust each new campaign radius or shift window during setup.</p></div>
              <Banknote size={20} />
            </section>
          </aside>
        </div>
      </DataPanel>
    </>
  );
}
